import { Typography } from '@hmsexp/component-library';
import React, { useCallback } from 'react';
import { View } from 'react-native';
import GridLayout from '../../../../components/GridLayout';
import { onlineReviewCompStyling } from './styles';
import SentimentChart from '../SentimentChart';
import { useSelector } from 'react-redux';
import OverviewLoader from '../../../Overview/components/OverviewComp/OverviewLoader';
import LowerSampleSizeFooter from '../../../../components/Common/LowerSampleSizeFooter';
import ScoreOverTime from './components/ScoreOverTime';
import MultiFeatureCard from '../../../../components/Common/Card/MultiOptionsCard/MultiFeatureCard';
import OnlineReviewByProperties from './components/OnlineReviewByProperties';
import ReviewRatings from './components/ReviewRatings';
import { makeObjectOutOfArrays } from '../../../Overview/utils';
import PieChartContainer from '../../../Overview/components/OverviewComp/components/PieChartContainer';
import ScoreBySource from './components/ScoreBySource';
import ReviewTopicPlot from '../../../Overview/components/OverviewComp/components/ReviewTopicPlot';
import { OVERVIEW_WIDGET_DATA } from '../../constants';
import { useHistory } from 'react-router-dom';
import get from 'lodash/get';

const OverviewComp = () => {
  const styles = onlineReviewCompStyling();
  const history = useHistory();
  const {
    isFetchingOnlineReviewData,
    OnlineReviewData = [],
    OverviewDataFetchingError
  } = useSelector(e => e.fetchOnlineReviewDataReducer);
  const { filtersLoading, applied_filter_values, applied_filters } = useSelector(e => e.filterReducer);
  const timeperiodLabel = get(applied_filters, 'timePeriod[0].fullLabel', 'Last 90 days');

  function pieChartTooptip(scoreSize) {
    return (
      <View style={styles.pieChartContainer}>
        <Typography textProps={{ style: styles.tooltipValue }} variant='button' color='white'>
          {scoreSize}
        </Typography>
        <Typography textProps={{ style: styles.tooltipLabel }} variant='button' color='white'>
          {timeperiodLabel}
        </Typography>
      </View>
    );
  }
  function onTopicClick(e) {
    const appliedFilterValues = { ...applied_filter_values, topic_filters: [[e]] };
    const stringified = encodeURIComponent(JSON.stringify(appliedFilterValues));
    history.push(`/dashboard/feedback?filters=${stringified}`);
  }

  const renderItem = useCallback(({ item }) => {
    switch (item.type) {
      case 'TEXT_HEADER':
        return <Typography {...item?.data?.typographyProps}>{item?.data?.title}</Typography>;
      case 'PIE_CHART':
        let { columns: pieColumns, metric_data: pieMetricData, metric_count } = item?.cstData || {};
        let pieDataObj = makeObjectOutOfArrays(pieColumns, pieMetricData?.[0]);
        let value = pieDataObj[item?.valueKeyToPlot];
        let benchmarkSampleSize = (value - pieDataObj?.benchmark_value)?.toFixed(2) || '0.00';
        const subLabel = `${benchmarkSampleSize > 0 ? '+' : '-'}  ${benchmarkSampleSize}`;
        const options = {
          ...item?.data,
          centerLabel: {
            label: value,
            subLabel: subLabel
          }
        };
        let pieChartSubtitle = `${timeperiodLabel}: ${item?.title}`;
        const lowSampleSize = item?.lowSampleSize
        const isLowSampleSize = get(metric_count, "[0][0]", null) < lowSampleSize;
        return (
          <MultiFeatureCard
            title={item?.title}
            subTitle={pieChartSubtitle}
            renderContent={(activeSwitch, contentRef) => (
              <PieChartContainer
                isLowSampleSize={isLowSampleSize}
                value={value}
                data={item}
                options={options}
                ref={contentRef}
                activeSwitch={activeSwitch}
                tooltipComponent={() => pieChartTooptip(subLabel)}
              />
            )}
            enableActionBtn={item?.enableActionBtn}
            style={{ childContainer: { paddingVertical: 20, paddingTop: 40 } }}
            enableSwitchTab={item?.enableSwitchTab}
            switchOptions={item?.switchOptions}
            renderFooter={() => (
              <View>
                {isLowSampleSize && <LowerSampleSizeFooter size={lowSampleSize} />}
                <Typography fontWeightType='regular' variant='button'>
                  {item?.footerText}
                </Typography>
              </View>
            )}
          />
        );
      case 'REVIEW_VOLUME':
        return (
          <MultiFeatureCard
            title={item?.title}
            subTitle={timeperiodLabel}
            style={{ childContainer: { margin: 'auto' } }}
            enableActionBtn={item?.enableActionBtn}
            enableSwitchTab={item?.enableSwitchTab}
            switchOptions={item?.switchOptions}
            renderContent={() => {
              return (
                <View style={{ alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                  <Typography variant='button' fontWeightType='regualar'>
                    {item?.data?.label}
                  </Typography>
                  <Typography textProps={{ style: { marginTop: 20 } }} variant='h3'>
                    {item?.cstData?.metric_data?.[0]?.[0] || '--'}
                  </Typography>
                </View>
              );
            }}
            renderFooter={() => (
              <Typography fontWeightType='regular' variant='button'>
                {item?.footerText}
              </Typography>
            )}
          />
        );
      case 'SCORE_BY_SOURCE':
        return (
          <MultiFeatureCard
            title={item?.title}
            subTitle={`Time Period: ${timeperiodLabel}`}
            style={{ childContainer: { paddingVertical: 20 } }}
            enableActionBtn={item?.enableActionBtn}
            enableSwitchTab={item?.enableSwitchTab}
            switchOptions={item?.switchOptions}
            renderContent={(activeSwitch, contentRef) => {
              return <ScoreBySource type={activeSwitch} lowSampleSize={item?.lowSampleSize} itemData={item} ref={contentRef} />;
            }}
            renderFooter={() => <LowerSampleSizeFooter size={item?.lowSampleSize} />}
          />
        );
      case 'SENTIMENT_CHART':
        return (
          <MultiFeatureCard
            title={item?.title}
            subTitle={timeperiodLabel}
            style={{ childContainer: { paddingVertical: 20 } }}
            enableActionBtn={true}
            enableSwitchTab={item?.enableSwitchTab || true}
            switchOptions={item?.switchOptions}
            renderContent={(_, contentRef) => {
              return <SentimentChart ref={contentRef} widgetData={item} data={item?.cstDataV2 || []} />;
            }}
            renderFooter={() => (
              <Typography fontWeightType='regular' variant='button'>
                {item?.footerText}
              </Typography>
            )}
          />
        );
      case 'score_over_time':
        return (
          <MultiFeatureCard
            title={item?.title}
            subTitle={`Time Period: ${timeperiodLabel}`}
            style={{ childContainer: { paddingVertical: 20 } }}
            enableActionBtn={item?.enableActionBtn}
            enableSwitchTab={item?.enableSwitchTab}
            switchOptions={item?.switchOptions}
            renderContent={(activeSwitch, contentRef) => {
              return <ScoreOverTime type={activeSwitch} lowSampleSize={item?.lowSampleSize} itemData={item} ref={contentRef} />;
            }}
            renderFooter={() => <LowerSampleSizeFooter size={item?.lowSampleSize} />}
          />
        );
      case 'TOP_BOTTOM_TOPICS':
        return (
          <MultiFeatureCard
            title={item?.title}
            subTitle={item?.subTitle}
            enableActionBtn={item?.enableActionBtn}
            enableSwitchTab={item?.enableSwitchTab}
            switchOptions={item?.switchOptions}
            renderContent={(activeSwitch, contentRef) => {
              return (
                <ReviewTopicPlot
                  onTopicClick={onTopicClick}
                  type={activeSwitch}
                  widgetData={item}
                  itemData={item?.cstDataV2}
                  contentRef={contentRef}
                />
              );
            }}
            renderFooter={() => (
              <Typography fontWeightType='regular' variant='button'>
                {item?.footerText}
              </Typography>
            )}
          />
        );
      case 'review_ratings_by_site':
        return (
          <MultiFeatureCard
            title={item?.title}
            subTitle={timeperiodLabel}
            style={{ childContainer: { paddingVertical: 20 } }}
            enableActionBtn={item?.enableActionBtn}
            enableSwitchTab={item?.enableSwitchTab}
            switchOptions={item?.switchOptions}
            renderContent={(activeSwitch, contentRef) => {
              return <ReviewRatings lowSampleSize={item?.lowSampleSize} type={activeSwitch} itemData={item} ref={contentRef} />;
            }}
            renderFooter={() => <LowerSampleSizeFooter size={item?.lowSampleSize} />}
          />
        );
      case 'ONLINE_REVIEW_BY_PROPERTIES':
        return (
          <OnlineReviewByProperties lowSampleSize={item?.lowSampleSize} item={{ ...item, subTitle: timeperiodLabel }} />

        );

      default:
        return null;
    }
  }, [timeperiodLabel])

  const renderContent = () => {
    if (isFetchingOnlineReviewData || filtersLoading) {
      return <OverviewLoader />;
    }
    return <GridLayout gap={30} data={OnlineReviewData} renderItem={renderItem} />;
    // return <GridLayout gap={30} data={OVERVIEW_WIDGET_DATA} renderItem={renderItem} />;
  };

  return <View style={{ padding: 20 }}>{renderContent()}</View>;
};
export default OverviewComp;
